import produce from 'immer';

import { refoldComponents } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import { ScreenStepConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { uiStepAllComponentConfigs } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  createTransitionComponent,
  createTransitionComponentPreview,
  terminalComponent,
  terminalComponentPreview,
} from 'dashboard/lib/stubs/inquiry-template-version';
import {
  InquiryTemplateVersionStepStatus,
  InquiryTemplateVersionStepUi,
  isCombinedStepConfig,
  isIntegrationStepConfig,
  isLoopStepConfig,
  isVerifyWithPersonaStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import {
  isButtonSubmitComponentConfig,
  isUiTerminalComponent,
  isUiTerminalComponentConfig,
  isUiTransitionComponent,
  isUiTransitionComponentConfig,
  TransitionConfigAttributes,
  UiComponentConfig,
  UiTransitionComponentConfig,
} from 'lib/interfaces/ui-components';
import { assertNever } from 'lib/utils/types';

/**
 * Removes ui step transition components for when inserting in a terminal position
 * Ensures that at least one terminal component (button complete) exists on the step
 * @param step ui step config
 * @returns new ui step config with no transition components
 */
export const uiStepTerminalUpdated = (
  step: InquiryTemplateVersionStepUi,
): InquiryTemplateVersionStepUi => {
  return produce(step, (draftUiStep) => {
    const nonTransitionComponents = uiStepAllComponentConfigs(step).filter(
      (componentConfig) => !isUiTransitionComponentConfig(componentConfig),
    );
    draftUiStep.config.components =
      nonTransitionComponents.find(isUiTerminalComponentConfig) != null
        ? refoldComponents(nonTransitionComponents)
        : refoldComponents(nonTransitionComponents).concat(terminalComponent);
  });
};

/**
 * Ensures that at least one terminal component (button complete) exists on the step preview
 * @param stepPreview ui step preview
 * @returns new ui step preview with updated components
 */
export const uiStepPreviewTerminalUpdated = (stepPreview: StepUi): StepUi => {
  return produce(stepPreview, (draftStepPreview) => {
    draftStepPreview.config.components =
      draftStepPreview.config.components.find(isUiTerminalComponent) != null
        ? draftStepPreview.config.components
        : draftStepPreview.config.components.concat(terminalComponentPreview);
  });
};

/**
 * @param step step config to update
 * @param transitionType 'step' or 'workflow'
 * @param targetToken main transition target - this should be a step name or workflow version id
 * @param workflowErrorStepName required for transition to workflow - fallback step when workflow errors
 * @param transitionComponentName optionally specify which ui step component to update transition config for
 */
type UpdateTransitionParams =
  | {
      step: ScreenStepConfig;
      transitionType: 'step';
      targetToken: string;
      workflowErrorStepName?: string;
      transitionComponentName?: string;
    }
  | {
      step: ScreenStepConfig;
      transitionType: 'workflow';
      targetToken: string;
      workflowErrorStepName: string;
      transitionComponentName?: string;
    };

export const stepTransitionUpdated = ({
  step,
  transitionType,
  targetToken,
  workflowErrorStepName,
  transitionComponentName,
}: UpdateTransitionParams): ScreenStepConfig => {
  switch (step.type) {
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.Loop: {
      return produce(step, (draftCombinedStep) => {
        draftCombinedStep.config = updatedTransitionConfig({
          transitionConfig: draftCombinedStep.config,
          transitionType,
          targetToken,
          workflowErrorStepName,
        });
      });
    }
    case UiStepType.Ui:
      return uiStepTransitionUpdated({
        step,
        transitionType,
        targetToken,
        workflowErrorStepName,
        transitionComponentName,
      });
    default:
      assertNever(step);
  }
};

/**
 * Updates the transition config values for a single ui step component -
 * either a specific component via the `transitionComponentName`, or the submit button, or the first transition component
 * Used in flow editor when adding or deleting steps
 * @param step ui step config
 * @returns new ui step config
 */
const uiStepTransitionUpdated = ({
  step,
  transitionComponentName,
  ...transitionParams
}: Omit<UpdateTransitionParams, 'step'> & {
  step: InquiryTemplateVersionStepUi;
}): InquiryTemplateVersionStepUi => {
  const allComponentConfigs = uiStepAllComponentConfigs(step);
  let targetComponentName = transitionComponentName;
  if (targetComponentName == null) {
    targetComponentName =
      allComponentConfigs.find(isButtonSubmitComponentConfig)?.name ||
      allComponentConfigs.find(isUiTransitionComponentConfig)?.name;
  }
  return produce(step, (draftUiStep) => {
    if (targetComponentName != null) {
      const updatedComponents = allComponentConfigs.map((componentConfig) =>
        isTargetTransitionComponent(componentConfig, targetComponentName)
          ? componentWithUpdatedTransition({
              transitionComponent: componentConfig,
              ...transitionParams,
            })
          : componentConfig,
      );
      draftUiStep.config.components = refoldComponents(updatedComponents);
    }
  });
};

const isTargetTransitionComponent = (
  componentConfig: UiComponentConfig,
  transitionComponentName: string,
): componentConfig is UiTransitionComponentConfig => {
  return (
    isUiTransitionComponentConfig(componentConfig) &&
    componentConfig.name === transitionComponentName
  );
};

export const componentWithUpdatedTransition = ({
  transitionComponent,
  transitionType,
  targetToken,
  workflowErrorStepName,
}: Omit<UpdateTransitionParams, 'step' | 'transitionComponentName'> & {
  transitionComponent: UiTransitionComponentConfig;
}): UiTransitionComponentConfig => {
  return produce(transitionComponent, (draftTransitionComponent) => {
    draftTransitionComponent.config = updatedTransitionConfig({
      transitionConfig: draftTransitionComponent.config,
      transitionType,
      targetToken,
      workflowErrorStepName,
    });
  });
};

/**
 * Helper for updating transition config values.
 * Used in flow editor when adding or deleting steps
 * @param transitionConfig shared attributes for transition object (combined step, button, etc)
 * @returns new config with updated values
 */
const updatedTransitionConfig = <T extends TransitionConfigAttributes>({
  transitionConfig,
  transitionType,
  targetToken,
  workflowErrorStepName,
}: Omit<UpdateTransitionParams, 'step' | 'transitionComponentName'> & {
  transitionConfig: T;
}): T => {
  return produce(transitionConfig, (draftTransitionConfig) => {
    if (transitionType === 'step') {
      draftTransitionConfig['to-step'] = targetToken;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftTransitionConfig['workflow-version-id'] = null;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftTransitionConfig['workflow-on-error-step-name'] = null;
    } else {
      draftTransitionConfig['workflow-version-id'] = targetToken;
      draftTransitionConfig['workflow-on-error-step-name'] = workflowErrorStepName;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftTransitionConfig['to-step'] = null;
    }
  });
};

/**
 * Filters all terminal components from ui step preview (client data).
 * If no transition components are found post-filtering, a placeholder
 * submit button is added to match the change made in `stepWithClearedTransitions`
 * Used in flow editor when storing data in clipboard for copy/cut.
 *
 * @param stepPreview ui step preview
 * @returns new ui step preview with updated components
 */
export const uiStepPreviewWithClearedTransitions = (stepPreview: StepUi): StepUi => {
  return produce(stepPreview, (draftStepPreview) => {
    const filteredComponents = draftStepPreview.config.components.filter(
      (component) => !isUiTerminalComponent(component),
    );
    draftStepPreview.config.components =
      filteredComponents.find(isUiTransitionComponent) != null
        ? filteredComponents
        : filteredComponents.concat(createTransitionComponentPreview());
  });
};

/**
 * Clears all transition config values for a step:
 * `to-step`, `workflow-version-id`, `workflow-on-error-step-name`
 *
 * Also filters out all terminal components (cancel, complete buttons)
 * to prepare the step to be inserted in a non-terminal location.
 *
 * If no transition components are found post-filtering, a placeholder
 * submit button is added to ensure downstream transition updates succeed.
 * Used in flow editor when storing data in clipboard for copy/cut.
 *
 * @param step screen step (ui or combined)
 * @returns new step config with cleared transition configs
 */
export const stepWithClearedTransitions = (step: ScreenStepConfig): ScreenStepConfig => {
  if (
    isCombinedStepConfig(step) ||
    isIntegrationStepConfig(step) ||
    isVerifyWithPersonaStepConfig(step) ||
    isLoopStepConfig(step)
  ) {
    return produce(step, (draftCombinedStep) => {
      draftCombinedStep.config = clearedTransitionConfig(draftCombinedStep.config);
    });
  } else {
    return produce(step, (draftUiStep) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftUiStep.config['back-step'] = null;
      const clearedTransitionComponents = uiStepAllComponentConfigs(step)
        .filter((componentConfig) => !isUiTerminalComponentConfig(componentConfig))
        .map((componentConfig) =>
          isUiTransitionComponentConfig(componentConfig)
            ? componentWithClearedTransition(componentConfig)
            : componentConfig,
        );
      draftUiStep.config.components =
        clearedTransitionComponents.find(isUiTransitionComponentConfig) != null
          ? refoldComponents(clearedTransitionComponents)
          : refoldComponents(clearedTransitionComponents).concat(createTransitionComponent());
    });
  }
};

export const componentWithClearedTransition = (
  transitionComponent: UiTransitionComponentConfig,
): UiTransitionComponentConfig => {
  return produce(transitionComponent, (draftTransitionComponent) => {
    draftTransitionComponent.config = clearedTransitionConfig(draftTransitionComponent.config);
  });
};

/**
 * Helper for clearing transition config values.
 * Used in flow editor when storing data in clipboard for copy/cut
 * @param transitionConfig shared attributes for transition object (combined step, button, etc)
 * @returns new config with updated values
 */
export const clearedTransitionConfig = <T extends TransitionConfigAttributes>(
  transitionConfig: T,
): T => {
  return produce(transitionConfig, (draftTransitionConfig) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    draftTransitionConfig['to-step'] = null;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    draftTransitionConfig['workflow-version-id'] = null;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    draftTransitionConfig['workflow-on-error-step-name'] = null;
  });
};

export const stepBackTransitionUpdated = ({
  step,
  backStepTarget,
}: {
  step: ScreenStepConfig;
  backStepTarget: string | undefined;
}): ScreenStepConfig => {
  return produce(step, (draftStep) => {
    if ('back-step' in draftStep.config) {
      draftStep.config['back-step'] = backStepTarget;
    }
  });
};

/**
 * Replaces step statuses with provided param
 * @param step screen step (ui or combined)
 * @param statuses list of ITV step statuses
 * @returns new step config with updated statuses
 */
export const stepWithUpdatedStatuses = ({
  step,
  statuses,
}: {
  step: ScreenStepConfig;
  statuses: InquiryTemplateVersionStepStatus[];
}): ScreenStepConfig => {
  return produce(step, (draftStep) => {
    draftStep.config.statuses = statuses;
  });
};

/**
 * Updates step config name and name-display
 * @param step screen step (ui or combined)
 * @param name step name - used as unique identifier
 * @param nameDisplay display name
 * @returns new step config with updated name values
 */
export const stepWithUpdatedNames = ({
  step,
  name,
  nameDisplay,
}: {
  step: ScreenStepConfig;
  name: string;
  nameDisplay?: string;
}): ScreenStepConfig => {
  return produce(step, (draftStep) => {
    draftStep.name = name;
    draftStep['name-display'] = nameDisplay ?? getDisplayNameForStep(step);
  });
};
